import { createContext, useContext } from 'react';
import type { ToolField } from '@/modules/back-testing/datasets/tools/tool-schema';
import type { ToolDefinitionAction } from '@/services/toolDefinition.service';
import type { CustomToolStep } from '@/types/custom-tool.types';
import type { BuilderPlace, BuildIssue, CustomToolDraft } from '../custom-tool-draft';
import type { SlugStatus } from '../use-custom-tools';
import type { StepTools } from '../use-step-tools';
import type { BuilderView } from './builder-view';

/** Build the steps first; name, slug and description come last, once there is something to describe. */
export type BuilderStage = 'build' | 'details';

/** What the builder rings and brings into view. */
export type BuilderFocus = BuilderPlace | null;

/** How each step ended in the last test run of the draft, keyed by step id. */
export type StepOutcomes = Record<string, { status: 'success' | 'simulated' | 'error' | 'skipped'; message?: string }>;

export interface CustomToolBuilderValue {
	draft: CustomToolDraft;
	/** Set once the tool is saved, which locks its slug. */
	toolId: string | null;
	/** Edits with the same `group` in quick succession (typing in one field) undo together. */
	change: (edit: (draft: CustomToolDraft) => CustomToolDraft, group?: string) => void;
	/** Steps back through the draft's edits; nothing is saved until Create or Save. */
	undo: { run: () => void; available: boolean };
	stage: BuilderStage;
	/** How the steps are shown while building; both views edit the same draft. */
	view: BuilderView;
	setView: (view: BuilderView) => void;
	/** Moving to details is refused while the steps are incomplete. */
	goTo: (stage: BuilderStage) => void;
	focus: BuilderFocus;
	focusOn: (focus: BuilderFocus) => void;
	addStep: (tool: ToolDefinitionAction) => void;
	stepTools: StepTools;
	/** A step tool's arguments from its schema, or null while the tool is unknown. */
	fieldsOf: (step: CustomToolStep) => ToolField[] | null;
	/** Some step changes data, so the whole tool counts as a write. */
	writes: boolean;
	/** What keeps the steps from being complete, and where. */
	buildIssue: BuildIssue | null;
	/** Running the unsaved draft once while building. */
	testing: {
		open: boolean;
		setOpen: (open: boolean) => void;
		outcomes: StepOutcomes | null;
		setOutcomes: (outcomes: StepOutcomes | null) => void;
	};
	slugStatus: SlugStatus;
	/** A name and description suggested from the steps; `succeeded` once one is applied on this visit. */
	suggestion: { ask: () => void; pending: boolean; failed: boolean; succeeded: boolean };
	save: {
		run: () => void;
		pending: boolean;
		/** What keeps the tool from saving, before anything is sent: its name, slug or description, or no change yet. */
		problem: string | null;
		error: string | null;
		slugError: string | null;
	};
}

export const CustomToolBuilderContext = createContext<CustomToolBuilderValue | null>(null);

export const useCustomToolBuilder = (): CustomToolBuilderValue => {
	const value = useContext(CustomToolBuilderContext);
	if (!value) throw new Error('CustomToolBuilder.* must be rendered inside <CustomToolBuilder>');
	return value;
};
